import type { ErrorCode } from '@famora/contracts'

export type FehlerCode = ErrorCode | 'netz' | 'unbekannt'

/**
 * Ist das Gerät nachweislich offline?
 *
 * ⚠️ Bewusst `=== false` und nicht `!onLine`. Node hat seit Fassung 21 ein
 * globales `navigator`, aber OHNE `onLine` - mit `!undefined` galt dort jeder
 * Schreibversuch als offline (genau daran scheiterten die Tests von `schicke`).
 * Der TypeScript-Typ behauptet `boolean`, die Laufzeit liefert `undefined`;
 * deshalb wird hier auf einen ehrlichen Typ umgehängt, statt die Lint-Regel
 * stillzulegen. Das `typeof` bleibt davor: wo `navigator` gar nicht deklariert
 * ist, wäre ein Zugriff ein ReferenceError, den `?.` nicht abfängt.
 */
export function istOffline(): boolean {
  const nav: { onLine?: boolean } | undefined =
    typeof navigator === 'undefined' ? undefined : navigator
  return nav?.onLine === false
}

/**
 * Ein Fehler, der die Fehlerhülle des Servers trägt.
 *
 * `angemeldet:false` ist das Signal, zur Anmeldung zu leiten - und es steht
 * bewusst NUR bei 401. Ein Netzabbruch bedeutet nicht, dass die Sitzung weg ist.
 */
export class ApiFehler extends Error {
  constructor(
    readonly code: FehlerCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiFehler'
  }

  get angemeldet(): boolean {
    return this.code !== 'unauthorized'
  }
}

export interface HoleOptionen {
  fetchImpl?: typeof fetch
  signal?: AbortSignal
}

interface Huelle {
  error?: { code?: string; message?: string; details?: unknown }
}

const BEKANNT: readonly string[] = [
  'unauthorized',
  'forbidden',
  'not_found',
  'validation',
  'conflict',
  'rate_limited',
]

async function auswerten<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  const typ = res.headers.get('content-type') ?? ''
  const istJson = typ.includes('application/json')

  if (res.ok) {
    return istJson ? ((await res.json()) as T) : (undefined as T)
  }

  if (istJson) {
    const rumpf = (await res.json()) as Huelle
    const code = rumpf.error?.code
    if (code && BEKANNT.includes(code)) {
      return Promise.reject(
        new ApiFehler(
          code as ErrorCode,
          rumpf.error?.message ?? 'Etwas ist schiefgegangen.',
          res.status,
          rumpf.error?.details,
        ),
      )
    }
  }
  // Keine Fehlerhülle: nichts erfinden, den Status nennen.
  return Promise.reject(
    new ApiFehler('unbekannt', `Der Server antwortete mit ${res.status}.`, res.status),
  )
}

export async function hole<T>(pfad: string, opts: HoleOptionen = {}): Promise<T> {
  const f = opts.fetchImpl ?? fetch
  try {
    const res = await f(pfad, {
      credentials: 'same-origin',
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    return await auswerten<T>(res)
  } catch (e) {
    if (e instanceof ApiFehler) throw e
    throw new ApiFehler('netz', 'Keine Verbindung. Bitte später erneut versuchen.', 0)
  }
}

export async function schicke<T>(
  methode: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  pfad: string,
  rumpf?: unknown,
  opts: HoleOptionen = {},
): Promise<T> {
  if (istOffline()) {
    throw new ApiFehler('netz', 'Offline: Schreibversuche sind nicht möglich.', 0)
  }
  const f = opts.fetchImpl ?? fetch
  try {
    const res = await f(pfad, {
      method: methode,
      credentials: 'same-origin',
      ...(rumpf !== undefined
        ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(rumpf) }
        : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    return await auswerten<T>(res)
  } catch (e) {
    if (e instanceof ApiFehler) throw e
    if (istOffline()) {
      throw new ApiFehler('netz', 'Offline: Schreibversuche sind nicht möglich.', 0)
    }
    throw new ApiFehler('netz', 'Keine Verbindung. Bitte später erneut versuchen.', 0)
  }
}
